import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useMemo, useState } from 'react';
import { Loading } from 'components/uikit/suspense/Loading';
import { Button, ButtonColor } from 'components/uikit/buttons/Button';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';

import styles from './ManageRoleModal.module.scss';
import { CustomModal } from 'components/uikit/modal/CustomModal';
import { QueryFactory } from 'services/api';
import { Field } from 'components/uikit/Field.tsx';
import { SignersModalBlock } from '../signersModalBlock/SignersModalBlock.tsx';
import { ComboBoxInput } from 'components/uikit/inputs/dropdown/ComboBoxInput.tsx';
import { errorToString } from 'helpers/error-helpers.ts';
import { Box } from '@mui/material';
import { useModal } from 'components/uikit/modal/useModal.tsx';
import { Trans } from 'react-i18next';
import { useSignatureBlockQueriesInvalidation } from 'pages/authorized/documents/documentList/signatureBlock/useSignatureBlockQueriesInvalidation.tsx';
import { showErrorToast } from 'components/uikit/toast/showToast.tsx';

type ManageRoleModalProps = {
  projectId: number;
  revisionId: number;
  projectRoleId?: number;
  onClose: () => void;
  isOpen: boolean;
};

export const ManageRoleModal: FC<ManageRoleModalProps> = (props) => {
  const i18n = useScopedTranslation('Component.ManageRoleModal');
  const buttonTitle = useScopedTranslation('Button.Title');
  const invalidateSignatureBlockQueries = useSignatureBlockQueriesInvalidation({
    revisionId: props.revisionId,
  });
  const [roleName, setRoleName] = useState<string | null>(null);
  const [roleNameInputError, setRoleNameInputError] = useState<string>();
  const [signerIdList, setSignerIdList] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const modal = useModal();

  const projectRoleQuery = QueryFactory.ProjectRolesQuery.useGetQuery(
    props.projectRoleId!,
    {
      enabled: !!props.projectRoleId && props.isOpen,
    },
  );

  useEffect(() => {
    if (projectRoleQuery.data) {
      setRoleName(projectRoleQuery.data.name);
    }
  }, [projectRoleQuery.data]);

  const projectSuggestedRoleNamesQuery =
    QueryFactory.ProjectRolesQuery.useGetSuggestedRoleNamesQuery(
      { projectId: props.projectId },
      { enabled: !!props.projectId && props.isOpen },
    );

  const roleNameOptions = useMemo(
    () => projectSuggestedRoleNamesQuery.data ?? [],
    [projectSuggestedRoleNamesQuery.data],
  );

  const isFormFilled =
    roleName !== null && roleName.trim().length > 0 && signerIdList.length > 0;
  const queryClient = useQueryClient();

  const onClose = () => {
    setRoleNameInputError('');
    setRoleName(null);
    props.onClose();
  };

  const save = async () => {
    setIsLoading(true);
    let projectRoleId = props.projectRoleId;

    try {
      if (props.projectRoleId === undefined) {
        const dto = await QueryFactory.ProjectRolesClient.create({
          projectId: props.projectId,
          name: roleName!,
        });
        projectRoleId = dto.id;
      } else {
        await QueryFactory.ProjectRolesClient.patch(projectRoleId!, {
          name: roleName?.trim()!,
        });
      }
      await queryClient.invalidateQueries({
        queryKey: QueryFactory.ProjectRolesQuery.getSuggestedRoleNamesQueryKey(
          props.projectId,
        ),
      });

      await invalidateSignatureBlockQueries();
    } catch (e) {
      setRoleNameInputError(errorToString(e));
      setIsLoading(false);
      return;
    }

    try {
      const documentNames = projectRoleId
        ? await QueryFactory.ProjectSignerClient.verifySignersUpdateAbility({
            projectRoleId: projectRoleId!,
            signerIds: signerIdList,
          })
        : [];
      if (documentNames.length > 0) {
        const result = await modal.showConfirm({
          title: i18n.t('change_confirm_title'),
          text: (
            <Trans
              i18nKey={'Component.ManageRoleModal.change_confirm_message'}
              values={{
                documents: documentNames.join(', '),
              }}
            />
          ),
        });
        if (result) {
          await QueryFactory.ProjectSignerClient.updateSigners({
            projectRoleId: projectRoleId!,
            signerIds: signerIdList,
          });
        } else {
          setIsLoading(false);
          return;
        }
      } else {
        await QueryFactory.ProjectSignerClient.updateSigners({
          projectRoleId: projectRoleId!,
          signerIds: signerIdList,
        });
      }
    } catch (e) {
      showErrorToast(e);
      setIsLoading(false);
      return;
    }

    await queryClient.invalidateQueries({
      queryKey: QueryFactory.ProjectRolesQuery.searchQueryKey(),
    });
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.ProjectRolesQuery.getQueryKey(
        props.projectRoleId!,
      ),
    });
    await queryClient.invalidateQueries({
      queryKey: QueryFactory.ProjectSignerQuery.searchQueryKey(),
    });
    await invalidateSignatureBlockQueries();
    setIsLoading(false);
    onClose();
  };

  const title = useMemo(() => {
    if (props.projectRoleId === undefined) {
      return i18n.t('title_new');
    } else {
      return i18n.t('title_edit');
    }
  }, [props.projectRoleId]);

  return (
    <CustomModal
      isOpen={props.isOpen}
      onClose={onClose}
      isBlocking={true}
      title={title}
    >
      <Loading
        loading={
          isLoading ||
          (projectRoleQuery.isLoading && !!props.projectRoleId) ||
          projectSuggestedRoleNamesQuery.isLoading
        }
        testId={'manage-role-modal'}
      >
        <div className={styles.main}>
          <Field title={i18n.t('role_input')} className={styles.field}>
            <ComboBoxInput
              testId={'manage-role-modal-name-input'}
              endAdornment={false}
              enableSearch={true}
              onValueChanged={async (value) => {
                setRoleName(value);
              }}
              options={roleNameOptions}
              value={roleName}
              errorText={roleNameInputError}
              placeholder={''}
              renderOption={(props, option) => (
                <Box
                  data-test-id={'signers-dropdown-button'}
                  component="li"
                  {...props}
                  key={window.crypto.randomUUID()}
                  className={styles.dropdownButton}
                >
                  {option}
                </Box>
              )}
            />
          </Field>
          <div className={styles.signersBlock}>
            <SignersModalBlock
              projectRoleId={props.projectRoleId}
              onUpdate={(ids) => {
                setSignerIdList(ids);
              }}
            />
          </div>
        </div>
        <div className={styles.footer}>
          <Button
            testId={'manage-role-modal-cancel-button'}
            className={styles.button}
            color={ButtonColor.Secondary}
            title={buttonTitle.t('cancel')}
            onClick={onClose}
          />
          <Button
            testId={'manage-role-modal-save-button'}
            className={styles.button}
            color={ButtonColor.Primary}
            type={'submit'}
            disabled={!isFormFilled}
            title={buttonTitle.t('save')}
            onClick={save}
          />
        </div>
      </Loading>
    </CustomModal>
  );
};
